import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { CategoryType } from '@signal/asp-data-commons';
import { first } from 'rxjs/operators';
import { ChecklistState } from '../../../store/checklist/checklist.reducer';
import * as fromSelector from '../../../store/checklist/checklist.selectors';
import * as fromActions from '../../../store/checklist/checklist.actions';
import { AlertService } from 'src/app/modules/shared/services/alert.service';
import { TranslateService } from '@ngx-translate/core';
import { SubscriptionList } from 'src/app/modules/shared/models/asp.types';
import { unsubscribeSubscriptions } from 'src/app/modules/shared/services/util.service';

@Component({
  selector: 'app-checklist-form',
  templateUrl: './checklist-form.component.html',
  styleUrls: ['./checklist-form.component.scss']
})
export class ChecklistFormComponent implements OnInit, OnDestroy {
  @Input('checklist') checklist: string;
  addonChecklist = new UntypedFormControl('');
  checklistForm: UntypedFormGroup;
  disableForm: boolean;
  subscriptions: SubscriptionList = {};
  @Output() formEdited:EventEmitter<boolean>=new EventEmitter();
  @Input() saveClicked:EventEmitter<boolean>=new EventEmitter();

  categories = [{ id: CategoryType.TECHNOLOGY, display: 'checklist.technology' }, 
  { id: CategoryType.GENERAL_FEATURES, display: 'checklist.generalFeatures' },
  { id: CategoryType.SAFETY, display: 'checklist.safety' },
  { id: CategoryType.VEHICLE_PUBLICATIONS, display: 'checklist.vehiclePublications' },
  { id: CategoryType.RECOMMENDED, display: 'opcodeServiceManagement.recommended' },
  { id: CategoryType.ACCESSORIES, display: 'checklist.accessories' }, 
  { id: CategoryType.DEALER_STAFF_INTRODUCTIONS, display: 'checklist.dealerStaffIntroductions' }]

  validatedFields = ['name', 'duration'];
  isEnglishText: boolean = true;

  constructor(private readonly formBuilder: UntypedFormBuilder,
    private readonly alertService: AlertService,
    private readonly checklistStore: Store<ChecklistState>,
    private readonly translateService: TranslateService) { 
      if(this.translateService.currentLang?.includes('es')){
        this.isEnglishText = false;
      }
    }

  ngOnInit(): void {
    this.disableForm = true;
    this.clearForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    const checklist = changes.checklist?.currentValue;
    if (!!checklist) {
      this.disableForm = false;
      setTimeout(() => this.viewSelectedOpcode());
    }
    else if (checklist === null) {
      this.disableForm = true;
      this.clearForm();
    }
    if(this.saveClicked)
    {
      this.submitForm();
    }
  }

  cancelForm() {
    if (!!this.checklist) {
      this.viewSelectedOpcode();
    } else {
      this.clearForm();
    }
  }

  clearForm() {
    this.checklistForm = this.formBuilder.group({
      checklistId: ['', [Validators.required]],
      displayName: ['', [Validators.required]],
      status: [false],
      autoSelect: [false],
      isDefault: [false],
      isMandatory: [false],
      isCorporateMandatory: [false],
      category: ['', Validators.required],
      description: [''],
      duration: [null, [Validators.required, Validators.min(1)]],
      userComments: [false],
      commentsRequired: [false],
      isChildChecklistEnabled: [false],
      childChecklist: this.formBuilder.array([]),
      SK: [''],
      PK: [''],
    });
    this.addonChecklist.patchValue('');
    // FormGroup Change events
    this.detectFormValueChanges();
  }

  get commentsRequiredField() {
    return this.checklistForm.get('commentsRequired');
  }

  get isChildChecklistEnabled() {
    return this.checklistForm.get('isChildChecklistEnabled');
  }

  get commentsEnabledField() {
    return this.checklistForm.get('userComments');
  }

  get isMandatory() {
    return this.checklistForm.get('isMandatory');
  }

  get isCorporateMandatory() {
    return this.checklistForm.get('isCorporateMandatory');
  }

  get isAutoSelect() {
    return this.checklistForm.get('autoSelect');
  }

  get isDefault() {
    return this.checklistForm.get('isDefault');
  }

  get childChecklistArray() {
    return this.checklistForm.controls.childChecklist as UntypedFormArray;
  }

  detectFormValueChanges() {
    this.subscriptions.commentSub = this.checklistForm.get('userComments').valueChanges.subscribe(t => {
      if (t) {
        this.commentsRequiredField.enable();
      }
      else {
        this.commentsRequiredField.patchValue(false);
        this.commentsRequiredField.disable();
      }
    });

    this.subscriptions.durationSub = this.checklistForm.get('duration').valueChanges.subscribe(val=> {
      //For Mandatory checklist item
      if(this.isMandatory.value && val < 10 && val > 0) {
        this.checklistForm.get('duration').setErrors({'Min10' : 'The duration of this item can not be below 10 mins'});
      }
    })

    this.subscriptions.checkbox = this.childChecklistArray.valueChanges.subscribe(t => {
      if (this.isChildChecklistEnabled.value) {
        this.calculateChildDuration();
      }
    })
    this.subscriptions.formChanges =  this.checklistForm.valueChanges.subscribe(()=>{
      this.formEdited.emit(!this.checklistForm.dirty);
    });
  }

  calculateChildDuration() {
    let duration = 0;
    this.childChecklistArray.value.forEach(list => {
      duration += list.duration ? list.duration : 0;
    })
    this.checklistForm.get('duration').patchValue(duration);
  }

  hasError(controlName: string, errorName: string) {
    return this.checklistForm.get(controlName).hasError(errorName);
  }

  hasTouched(controlName: string) {
    return this.checklistForm.get(controlName).touched;
  }

  hasChildItemsError(errorName: string, i: number) {
    (this.checklistForm.get('childChecklist') as UntypedFormArray).controls[i].hasError(errorName);
  }

  updateSwitchValues(val: boolean, property: string) {
    switch (property) {
      case 'status':
        this.checklistForm.get(property).patchValue(val);
        break;

      case 'autoSelect':
        this.checklistForm.get(property).patchValue(val);
        if (val) {
          this.checklistForm.get('status').patchValue(val);
          this.mandateChildItem();
        }
        break;

      case 'isMandatory':
        this.checklistForm.get(property).patchValue(val);
        if (val) {
          this.checklistForm.get('status').patchValue(val);
          this.checklistForm.get('autoSelect').patchValue(val);
          this.mandateChildItem();
        }
        break;

      default:
        break;
    }
    this.checklistForm.markAsDirty();
    this.formEdited.emit(!this.checklistForm.dirty);
  }

  patchChildChecklist(checklist) {
    this.childChecklistArray.clear();
    if (checklist && checklist.length) {
      checklist.forEach((item, i) => {
        if (!this.childChecklistArray.controls[i]) {
          this.childChecklistArray.push(new UntypedFormGroup({
            id: new UntypedFormControl(item.id),
            isDefault: new UntypedFormControl(item.isDefault),
            name: new UntypedFormControl(item.name),
            duration: new UntypedFormControl(item.duration),
            online: new UntypedFormControl(item.online),
            autoSelect: new UntypedFormControl(item.autoSelect),
          }));
        } else {
          this.childChecklistArray.controls[i].patchValue(new UntypedFormGroup({
            id: new UntypedFormControl(item.id),
            isDefault: new UntypedFormControl(item.isDefault),
            name: new UntypedFormControl(item.name),
            duration: new UntypedFormControl(item.duration),
            online: new UntypedFormControl(item.online),
            autoSelect: new UntypedFormControl(item.autoSelect),
          }));
        }
        this.childChecklistArray.controls[i].setValidators(this.setChildItemsValidator());
      })
    }
  }

  mandateChildItem() {
    if (this.childChecklistArray.value.length) {
      this.childChecklistArray.controls.forEach((a, i) => {
        if(a.get('online').value) {
          a.get('autoSelect').patchValue(true);
        }
      });
    }
  }

  hasDefaultSubItem() {
    return this.childChecklistArray.value.length > 0 && this.childChecklistArray.value.filter(x=> x?.isDefault && x.online).length > 0;
  }

  setChildItemsValidator(): ValidatorFn {
    return (item: UntypedFormGroup): ValidationErrors => {

      if (this.isChildChecklistEnabled.value) {
        this.validatedFields.forEach(fields => {
          if (!item.controls[fields].value) {
            item.controls[fields].setErrors({ required: `${fields} ${this.translateService.instant('service.isRequired')}` });
          }
        });
      }
      return;
    };
  }

  removeChildItemsValidator(index: number) {
    const item = this.childChecklistArray.controls[index] as UntypedFormGroup;
    this.validatedFields.forEach(field => item.controls[field].setErrors(null, { emitEvent: false }));
  }

  onChildChecklistChecked() {
    if (this.childChecklistArray.value.length) {
      this.childChecklistArray.controls.forEach((a, i) => {
        if (this.isChildChecklistEnabled.value) {
          a.setValidators(this.setChildItemsValidator());
          a.updateValueAndValidity();
        } else {
          this.removeChildItemsValidator(i);
        }
      });
    }
  }

  onSubItemAutoSelectChange(val: boolean, index: number) {
    if(val) {
      this.childChecklistArray.controls[index].get('online').patchValue(val);
    }
  }

  onSubItemOnlineChange(val: boolean, index: number) {
    if(val && (this.isMandatory.value || this.isAutoSelect.value)) {
        this.childChecklistArray.controls[index].get('autoSelect').patchValue(val);
    }
  }

  viewSelectedOpcode() {
    this.checklistStore.dispatch(new fromActions.LoadChecklist({ checklist: this.checklist }));
    this.subscriptions.selectItemSub = this.checklistStore.select(fromSelector.selectFromChecklist).pipe(first()).subscribe(checklist => {
      this.clearForm();
      this.checklistForm.patchValue(checklist);
      this.patchChildChecklist(checklist.childChecklist);
      this.checklistForm.markAllAsTouched();
    });
  }

  onDeleteChildItem(index: number) {
    this.childChecklistArray.removeAt(index);
  }

  onAddChecklist() {
    if (this.childChecklistArray.value.findIndex(x => x.name.toLowerCase() === this.addonChecklist.value.toLowerCase()) === -1) {
      this.childChecklistArray.push(new UntypedFormGroup({
        id: new UntypedFormControl(''),
        isDefault: new UntypedFormControl(false),
        name: new UntypedFormControl(this.addonChecklist.value),
        duration: new UntypedFormControl(null),
        online: new UntypedFormControl(false),
        autoSelect: new UntypedFormControl(false),
      }));
      const index = this.childChecklistArray.length - 1;
      (this.childChecklistArray.controls[index] as UntypedFormGroup).setValidators(this.setChildItemsValidator());
      (this.childChecklistArray.controls[index] as UntypedFormGroup).markAllAsTouched();

    } else {
      this.alertService.showAlert('checklist.checklistItemAlreadyExists');
    }
    this.addonChecklist.patchValue('');
  }

  submitForm() {
    if (this.checklistForm.valid) {
      const value = {
        ...this.checklistForm.value,
        commentsRequired: this.commentsRequiredField.value
      }
      this.checklistStore.dispatch(new fromActions.UpdateChecklist({ checklist: value }));
    }
    this.formEdited.emit(true);
  }

  getCategoryTranslation(category: string) {
    switch (category) {
      case CategoryType.TECHNOLOGY:
        return 'checklist.technology';
      case CategoryType.GENERAL_FEATURES:
        return 'checklist.generalFeatures';
      case CategoryType.SAFETY:
        return 'checklist.safety';
      case CategoryType.VEHICLE_PUBLICATIONS:
        return 'checklist.vehiclePublications';
      case CategoryType.RECOMMENDED:
        return 'opcodeServiceManagement.recommended';
      case CategoryType.ACCESSORIES:
        return 'checklist.accessories';
      case CategoryType.DEALER_STAFF_INTRODUCTIONS:
        return 'checklist.dealerStaffIntroductions';
      default:
        return category;
    }
  }

  ngOnDestroy() {
    unsubscribeSubscriptions(this.subscriptions);
  }

}
